import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowLeft,
  ArrowRight,
  ChevronLeft,
  ChevronRight,
  X,
  ExternalLink,
  Calendar,
  User,
  Layers,
} from "lucide-react";
import { projects, profile } from "../../data/portfolio";

/* ─────────────────────────────────────────────
   Lightbox
───────────────────────────────────────────── */
const Lightbox = ({ images, startIndex, onClose }) => {
  const [idx, setIdx] = useState(startIndex);

  const prev = useCallback(
    () => setIdx((i) => (i - 1 + images.length) % images.length),
    [images.length]
  );
  const next = useCallback(
    () => setIdx((i) => (i + 1) % images.length),
    [images.length]
  );

  useEffect(() => {
    const handler = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowLeft") prev();
      if (e.key === "ArrowRight") next();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [onClose, prev, next]);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.3 }}
      className="fixed inset-0 z-[100] bg-black/95 backdrop-blur-xl flex items-center justify-center"
      data-testid="lightbox-overlay"
      onClick={onClose}
    >
      {/* Close */}
      <button
        onClick={onClose}
        data-testid="lightbox-close"
        className="absolute top-6 right-6 p-2 border border-neutral-700 hover:border-neutral-100 text-neutral-300 hover:text-neutral-100 transition-colors z-10"
        aria-label="Close lightbox"
      >
        <X size={20} />
      </button>

      {/* Counter */}
      <span className="absolute top-6 left-1/2 -translate-x-1/2 font-mono text-xs uppercase tracking-[0.2em] text-neutral-500">
        {idx + 1} / {images.length}
      </span>

      {/* Image */}
      <AnimatePresence mode="wait">
        <motion.img
          key={idx}
          src={images[idx]}
          alt={`View ${idx + 1}`}
          initial={{ opacity: 0, scale: 0.97 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.97 }}
          transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
          className="max-w-[90vw] max-h-[85vh] object-contain"
          onClick={(e) => e.stopPropagation()}
          data-testid="lightbox-image"
        />
      </AnimatePresence>

      {/* Prev / Next */}
      {images.length > 1 && (
        <>
          <button
            onClick={(e) => { e.stopPropagation(); prev(); }}
            data-testid="lightbox-prev"
            className="absolute left-4 md:left-8 p-3 border border-neutral-700 hover:border-neutral-100 text-neutral-300 hover:text-neutral-100 transition-colors"
            aria-label="Previous image"
          >
            <ChevronLeft size={20} />
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); next(); }}
            data-testid="lightbox-next"
            className="absolute right-4 md:right-8 p-3 border border-neutral-700 hover:border-neutral-100 text-neutral-300 hover:text-neutral-100 transition-colors"
            aria-label="Next image"
          >
            <ChevronRight size={20} />
          </button>
        </>
      )}

      {/* Thumbnail strip */}
      {images.length > 1 && (
        <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex gap-2 max-w-[90vw] overflow-x-auto pb-1">
          {images.map((src, i) => (
            <button
              key={i}
              onClick={(e) => { e.stopPropagation(); setIdx(i); }}
              data-testid={`lightbox-thumb-${i}`}
              className={`shrink-0 w-14 h-10 overflow-hidden border transition-colors duration-200 ${
                i === idx ? "border-neutral-100" : "border-neutral-700 opacity-50 hover:opacity-80"
              }`}
            >
              <img src={src} alt="" className="w-full h-full object-cover" />
            </button>
          ))}
        </div>
      )}
    </motion.div>
  );
};

/* ─────────────────────────────────────────────
   Gallery grid
───────────────────────────────────────────── */
const Gallery = ({ images, slug }) => {
  const [lightboxIdx, setLightboxIdx] = useState(null);

  return (
    <>
      {/* Main + grid */}
      <div className="grid grid-cols-1 gap-3">
        {/* Hero image */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          className="relative aspect-[16/9] overflow-hidden bg-neutral-950 border border-neutral-900 cursor-zoom-in group"
          onClick={() => setLightboxIdx(0)}
          data-testid={`gallery-hero-${slug}`}
        >
          <img
            src={images[0]}
            alt="Main view"
            className="w-full h-full object-cover group-hover:scale-[1.02] transition-transform duration-700 ease-out"
          />
          <div className="absolute inset-0 bg-black/10 group-hover:bg-black/0 transition-colors duration-500" />
          <div className="absolute bottom-4 right-4 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
            <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-300 bg-black/70 backdrop-blur px-2 py-1">
              Click to enlarge
            </span>
          </div>
        </motion.div>

        {/* Thumbnail grid */}
        {images.length > 1 && (
          <div
            className={`grid gap-3 ${
              images.length === 2
                ? "grid-cols-1"
                : images.length <= 4
                ? "grid-cols-3"
                : "grid-cols-4 md:grid-cols-5"
            }`}
          >
            {images.slice(1).map((src, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{
                  duration: 0.6,
                  ease: [0.16, 1, 0.3, 1],
                  delay: 0.05 * (i + 1),
                }}
                className="relative aspect-[4/3] overflow-hidden bg-neutral-950 border border-neutral-900 cursor-zoom-in group"
                onClick={() => setLightboxIdx(i + 1)}
                data-testid={`gallery-thumb-${slug}-${i}`}
              >
                <img
                  src={src}
                  alt={`View ${i + 2}`}
                  className="w-full h-full object-cover group-hover:scale-[1.04] transition-transform duration-700 ease-out"
                />
                <div className="absolute inset-0 bg-black/20 group-hover:bg-black/0 transition-colors duration-300" />
              </motion.div>
            ))}
          </div>
        )}
      </div>

      {/* Lightbox */}
      <AnimatePresence>
        {lightboxIdx !== null && (
          <Lightbox
            images={images}
            startIndex={lightboxIdx}
            onClose={() => setLightboxIdx(null)}
          />
        )}
      </AnimatePresence>
    </>
  );
};

/* ─────────────────────────────────────────────
   Main ProjectDetail page
───────────────────────────────────────────── */
const ProjectDetail = () => {
  const { slug } = useParams();
  const navigate = useNavigate();

  const projectIndex = projects.findIndex((p) => p.slug === slug);
  const project = projects[projectIndex];
  const prevProject = projects[projectIndex - 1] || null;
  const nextProject = projects[projectIndex + 1] || null;

  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [slug]);

  // Dynamic SEO: title + meta per project
  useEffect(() => {
    if (!project) return;
    const prevTitle = document.title;
    const prevDesc = document.querySelector('meta[name="description"]')?.getAttribute("content");

    document.title = `${project.title} — Ahmad Ilyas`;

    let metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) metaDesc.setAttribute("content", project.tagline);

    let ogTitle = document.querySelector('meta[property="og:title"]');
    if (ogTitle) ogTitle.setAttribute("content", `${project.title} — Ahmad Ilyas`);

    let ogDesc = document.querySelector('meta[property="og:description"]');
    if (ogDesc) ogDesc.setAttribute("content", project.tagline);

    let ogImg = document.querySelector('meta[property="og:image"]');
    if (ogImg && project.images?.[0]?.startsWith("/")) {
      ogImg.setAttribute("content", `${window.location.origin}${project.images[0]}`);
    }

    return () => {
      document.title = prevTitle;
      if (metaDesc && prevDesc) metaDesc.setAttribute("content", prevDesc);
    };
  }, [project]);

  if (!project) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] flex flex-col items-center justify-center gap-6 text-neutral-400">
        <span className="font-mono text-xs uppercase tracking-[0.2em]">404 — Project not found</span>
        <Link
          to="/"
          className="font-mono text-xs uppercase tracking-[0.2em] text-neutral-100 border border-neutral-700 hover:border-neutral-100 px-4 py-2 transition-colors"
        >
          ← Back home
        </Link>
      </div>
    );
  }

  return (
    <div
      className="min-h-screen bg-[#0a0a0a] text-neutral-100"
      data-testid={`project-detail-${slug}`}
    >
      {/* ── Minimal Navbar ── */}
      <motion.header
        initial={{ opacity: 0, y: -16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
        className="fixed top-0 inset-x-0 z-50 bg-black/60 backdrop-blur-xl border-b border-neutral-900"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12 h-16 md:h-20 flex items-center justify-between">
          <Link
            to="/"
            data-testid="detail-nav-home"
            className="font-mono text-sm tracking-wider text-neutral-100 inline-flex items-center gap-3"
          >
            <span className="inline-block w-2 h-2 bg-neutral-100" />
            <span>{profile.initials}</span>
            <span className="hidden sm:inline text-neutral-500">/ {profile.name}</span>
          </Link>

          <button
            onClick={() => navigate(-1)}
            data-testid="detail-nav-back"
            className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em] text-neutral-400 hover:text-neutral-100 transition-colors"
          >
            <ArrowLeft size={14} />
            Back
          </button>
        </div>
      </motion.header>

      {/* ── Page Content ── */}
      <div className="pt-32 md:pt-40 pb-24 md:pb-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12">

          {/* ── Hero header ── */}
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
            className="mb-12 md:mb-16"
          >
            {/* Breadcrumb */}
            <div className="flex items-center gap-3 mb-6">
              <Link
                to="/#work"
                className="font-mono text-xs uppercase tracking-[0.2em] text-neutral-500 hover:text-neutral-300 transition-colors"
              >
                Work
              </Link>
              <span className="text-neutral-700 font-mono text-xs">→</span>
              <span className="font-mono text-xs uppercase tracking-[0.2em] text-neutral-400">
                {String(projectIndex + 1).padStart(2, "0")}
              </span>
            </div>

            <h1
              className="font-serif text-4xl sm:text-5xl md:text-6xl tracking-tight leading-[1.02] text-neutral-100 mb-4 max-w-4xl"
              data-testid="detail-title"
            >
              {project.title}
            </h1>
            <p className="font-serif italic text-xl md:text-2xl text-neutral-400 leading-snug max-w-2xl">
              {project.tagline}
            </p>
          </motion.div>

          {/* ── Meta strip ── */}
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1], delay: 0.1 }}
            className="grid grid-cols-2 md:grid-cols-3 gap-px border border-neutral-900 bg-neutral-900 mb-12 md:mb-16"
            data-testid="detail-meta"
          >
            <div className="bg-[#0a0a0a] px-6 py-5 flex flex-col gap-1">
              <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-600 flex items-center gap-2">
                <Calendar size={10} /> Year
              </span>
              <span className="font-mono text-sm text-neutral-100">{project.year}</span>
            </div>
            <div className="bg-[#0a0a0a] px-6 py-5 flex flex-col gap-1">
              <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-600 flex items-center gap-2">
                <User size={10} /> Role
              </span>
              <span className="font-mono text-sm text-neutral-100">{project.role}</span>
            </div>
            <div className="bg-[#0a0a0a] px-6 py-5 col-span-2 md:col-span-1 flex flex-col gap-1">
              <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-600 flex items-center gap-2">
                <Layers size={10} /> Stack
              </span>
              <span className="font-mono text-sm text-neutral-300">
                {project.stack.join(" · ")}
              </span>
            </div>
          </motion.div>

          {/* ── Main content: gallery + sidebar ── */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-16">

            {/* Gallery — left 8 cols */}
            <div className="lg:col-span-8">
              <Gallery images={project.images} slug={project.slug} />
            </div>

            {/* Sidebar — right 4 cols */}
            <div className="lg:col-span-4 space-y-10">
              {/* Description */}
              <motion.div
                initial={{ opacity: 0, x: 16 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1], delay: 0.15 }}
              >
                <h2 className="font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-600 mb-4">
                  Overview
                </h2>
                <p className="text-neutral-300 leading-relaxed text-base">
                  {project.description}
                </p>
              </motion.div>

              {/* Divider */}
              <div className="border-t border-neutral-900" />

              {/* Features */}
              <motion.div
                initial={{ opacity: 0, x: 16 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1], delay: 0.2 }}
              >
                <h2 className="font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-600 mb-4">
                  Key Features
                </h2>
                <ul className="space-y-3">
                  {project.features.map((f, i) => (
                    <motion.li
                      key={i}
                      initial={{ opacity: 0, x: 12 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{
                        duration: 0.5,
                        ease: [0.16, 1, 0.3, 1],
                        delay: 0.25 + i * 0.06,
                      }}
                      className="flex items-start gap-3 text-sm text-neutral-300"
                    >
                      <span className="text-neutral-700 font-mono text-xs mt-1.5 shrink-0">▍</span>
                      <span>{f}</span>
                    </motion.li>
                  ))}
                </ul>
              </motion.div>

              {/* Divider */}
              <div className="border-t border-neutral-900" />

              {/* Tech stack */}
              <motion.div
                initial={{ opacity: 0, x: 16 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1], delay: 0.3 }}
              >
                <h2 className="font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-600 mb-4">
                  Tech Stack
                </h2>
                <div className="flex flex-wrap gap-2">
                  {project.stack.map((t) => (
                    <span
                      key={t}
                      className="border border-neutral-800 px-3 py-1.5 font-mono text-[11px] uppercase tracking-wider text-neutral-300"
                    >
                      {t}
                    </span>
                  ))}
                </div>
              </motion.div>

              {/* Divider */}
              <div className="border-t border-neutral-900" />

              {/* Image count badge */}
              <motion.div
                initial={{ opacity: 0, x: 16 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1], delay: 0.35 }}
                className="flex items-center gap-3"
              >
                <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-600">
                  Gallery
                </span>
                <span className="border border-neutral-800 px-2 py-0.5 font-mono text-[10px] text-neutral-400">
                  {project.images.length} screenshot{project.images.length > 1 ? "s" : ""}
                </span>
              </motion.div>
            </div>
          </div>

          {/* ── Project Navigation ── */}
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
            className="mt-24 md:mt-32 pt-10 border-t border-neutral-900 grid grid-cols-2 gap-4"
            data-testid="detail-project-nav"
          >
            {prevProject ? (
              <Link
                to={`/work/${prevProject.slug}`}
                data-testid="detail-prev-project"
                className="group flex flex-col gap-3 p-6 border border-neutral-900 hover:border-neutral-700 transition-colors duration-300"
              >
                <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-600 flex items-center gap-2">
                  <ArrowLeft size={10} /> Previous
                </span>
                <span className="font-serif text-lg text-neutral-300 group-hover:text-neutral-100 transition-colors leading-snug">
                  {prevProject.title.split("—")[0].trim()}
                </span>
              </Link>
            ) : (
              <div />
            )}

            {nextProject ? (
              <Link
                to={`/work/${nextProject.slug}`}
                data-testid="detail-next-project"
                className="group flex flex-col gap-3 p-6 border border-neutral-900 hover:border-neutral-700 transition-colors duration-300 text-right items-end ml-auto w-full"
              >
                <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-600 flex items-center gap-2">
                  Next <ArrowRight size={10} />
                </span>
                <span className="font-serif text-lg text-neutral-300 group-hover:text-neutral-100 transition-colors leading-snug">
                  {nextProject.title.split("—")[0].trim()}
                </span>
              </Link>
            ) : (
              <div />
            )}
          </motion.div>

          {/* ── Back to all work ── */}
          <div className="mt-12 flex justify-center">
            <Link
              to="/#work"
              data-testid="detail-back-to-work"
              className="inline-flex items-center gap-3 font-mono text-xs uppercase tracking-[0.2em] text-neutral-500 hover:text-neutral-100 border border-neutral-800 hover:border-neutral-100 px-6 py-3 transition-colors duration-300"
            >
              <ArrowLeft size={12} />
              All Projects
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProjectDetail;
